import { Component, InjectionToken, FactoryProvider, ViewChild, ElementRef, Injector, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { BaseControlComponent } from '../base-control/base-control.component';
import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
// For GIS lookups
import { GISControllerService } from "../../services/GIS-controller.service";
import { TextboxController } from './controllers/textbox-controller.interface';
import { GISController } from './controllers/GIS.controller';
import { Controllable } from '../../component-base/controller/controllable.interface';
import { CurrencyController } from './controllers/currency.controller';
import { NumberController } from './controllers/number.controller';
import { DateController } from './controllers/date.controller';
import { ControllerFactory } from '../../component-base/controller/controller.factory';
import { NumberSettingsComponent } from '../../../builder/components/state/controls/config/common/controller-selector/settings/number-settings/number-settings.component';
import { GISSettingsComponent } from "../../../builder/components/state/controls/config/common/controller-selector/settings/gis-settings/gis-settings.component";
import { NgxMaskDirective, NgxMaskPipe, IConfig } from 'ngx-mask';
import { DatePipe } from '@angular/common';
import { DefaultController } from './controllers/default.controller';
import { LoaderService } from "../../../common/services/loader.service";
import { ContextInteractionService } from '../../services/context-interaction.service';

let defaultControllerFactory = () => { return new DefaultController() };
let GISControllerFactory = (gisControllerService: GISControllerService, loaderService: LoaderService) => {
    return new GISController(gisControllerService) };
let CurrencyControllerFactory = () => { return new CurrencyController() };
let NumberControllerFactory = () => { return new NumberController() };
let DateControllerFactory = () => { return new DateController() };

export const DEFAULT_CONTROLLER = new InjectionToken<TextboxController>('Default');
export const GIS_CONTROLLER = new InjectionToken<TextboxController>('GIS');
export const CURRENCY_CONTROLLER = new InjectionToken<TextboxController>('Currency');
export const NUMBER_CONTROLLER = new InjectionToken<TextboxController>('Number');
export const DATE_CONTROLLER = new InjectionToken<TextboxController>('Date');

export const TEXTBOX_CONTROLLERS = [
    {
        provide: DEFAULT_CONTROLLER, useFactory: defaultControllerFactory
    },
    {
        provide: GIS_CONTROLLER,
        useFactory: GISControllerFactory,
        deps: [GISControllerService],
        settings: [GISSettingsComponent]
    },
    {
        provide: CURRENCY_CONTROLLER,
        useFactory: CurrencyControllerFactory,
        settings: [NumberSettingsComponent]
    },
    {
        provide: NUMBER_CONTROLLER,
        useFactory: NumberControllerFactory,
        settings: [NumberSettingsComponent]
    },
    {
        provide: DATE_CONTROLLER,
        useFactory: DateControllerFactory,
        deps: [DatePipe]
    }
];

@Component({
    selector: 'app-textbox',
    templateUrl: './textbox.component.html',
    styleUrls: ['./textbox.component.scss'],
    providers: [...TEXTBOX_CONTROLLERS],
    standalone: false
})
export class TextboxComponent extends BaseControlComponent implements Controllable, AfterViewInit, OnInit, OnDestroy {
    @ViewChild('input')
    inputView: ElementRef;
    @ViewChild(NgxMaskDirective)
	maskDir: NgxMaskDirective;

    controllers: FactoryProvider[] = TEXTBOX_CONTROLLERS;
    static componentName = 'TextboxComponent';
    controller: TextboxController;
    mask: string = '';
    showMask: boolean = false;
    dropMaskSpecialCharacters: boolean = false;
    specialCharacters: string[] = ["-", ","];
    allowNegativeNumbers: boolean = true;

    prefix: string = '';
    valueSubscription: Subscription;
	private _contextAlteredSubscription: Subscription;
	thousandSeparator: string = ",";
	private get iconfig(): Partial<IConfig> {
		return {
			thousandSeparator: this.thousandSeparator,
			allowNegativeNumbers: this.allowNegativeNumbers
		};
	}
    

    get maskedInput(): any {
        let maskedValue: any;
        if (this.componentData != null && this.componentData.config.controller != null && this.componentData.config.controller.controllerName != null && this.componentData.config.controller.controllerName.toLocaleLowerCase() === 'currency') {
            let value = this.contextValue;
            if (value == null) value = '';
            if (isNaN(value)) { //ensure commas and currency types aren't in the string before passing it through the pipe
                value = value.toString().replace(/\$/g, '');
                value = value.replace(/,/g, '');
            }
            maskedValue = this.currencyPipe.transform(value, 'USD', 'symbol', '1.2-2');
        } else {
            if (this.mask == null || this.mask == '') return this.contextValue;
            let value = isNaN(this.contextValue) ? this.contextValue : this.contextValue.toString();
			maskedValue = `${this.prefix}${this.maskPipe.transform(value, this.mask, this.iconfig)}`;
        }
        
        return maskedValue;
    }

   

    constructor(
        public _injector: Injector,
        readonly contextService: ContextInteractionService,
        private maskPipe: NgxMaskPipe,
        private currencyPipe: CurrencyPipe
    ) {
        super(contextService);
        this.controller = ControllerFactory.createController<TextboxController>(DEFAULT_CONTROLLER, this._injector, TEXTBOX_CONTROLLERS).controller;
    }

    ngOnInit() {
        super.ngOnInit();

        this._contextAlteredSubscription = this.contextService.contextTargetHasBeenSet.subscribe((alteredContextEvent) => {
            //If our context was changed, refetch it
            if (alteredContextEvent.target === this.componentData.contextTarget || alteredContextEvent.target === (this.componentData.contextTarget + ".value"))
                this.control.patchValue(alteredContextEvent.value);
        },
        (error) => {
            console.error(error);
        });

        this.control.patchValue(this.contextValue);
        this.valueSubscription = this.control.valueChanges.pipe(debounceTime(500)).subscribe(
            (value) => {
                if (value !== this.contextValue) {
                    this.setContextValue(value);
                    if (this.control && this.control.validator) {
                        super.change();
                    } 
                }                           
            },
            (error) => { console.error(error); }
        );
    }

    ngOnDestroy() {
        if (this.controller && this.controller.onDestroy) {
            this.controller.onDestroy();
        }
        if (this.valueSubscription) this.valueSubscription.unsubscribe();
        if (this._contextAlteredSubscription)
            this._contextAlteredSubscription.unsubscribe();
    }

    onFocus() {
        if (this.controller && this.controller.focus) {
            this.controller.focus();
        }
    }

    onBlur() {
        let value = this.control.value;
        this.setContextValue(value);
        if (this.controller && this.controller.blur) {
            this.controller.blur();
        }
        super.change(); 
    }

    ngAfterViewInit() {
        if (this.controller && this.controller.afterViewInit)
            this.controller.afterViewInit();
    }
}
